import React, { useEffect, useMemo, useRef, useState } from 'react';

import { isValidUrl } from '../../../../constants/url';
import wikiBoxInfoBoxItemType from '../../../../constants/wikiBoxInfoBoxItemType';
import ChopLinesExtended from '../../ChopLinesExtended/ChopLinesExtended';
import WikiBoxInfoBoxCoordinatesLinkItem from './WikiBoxInfoBoxCoordinatesLinkItem';
import WikiBoxInfoBoxLinkItem from './WikiBoxInfoBoxLinkItem';
import WikiBoxInfoBoxLinkRichItem from './WikiBoxInfoBoxLinkRichItem';
import WikiBoxInfoBoxListItem from './WikiBoxInfoBoxListItem';
import WikiBoxInfoBoxTextItem from './WikiBoxInfoBoxTextItem';

type WikiBoxInfoBoxItemProps = {
    data: IWikiBoxInfoboxRow;
    encryptionDetails?: EncryptionDetailsType;
    openInNewTab?: boolean;
    onShowMore: () => {};
    shouldToggleChoplines: boolean;
    onChoplinesToggled: () => void;
    makeSearch: (input) => {};
};
export default function WikiBoxInfoBoxItem({
    data,
    encryptionDetails,
    openInNewTab,
    onShowMore,
    shouldToggleChoplines,
    onChoplinesToggled,
    makeSearch,
    topicTitle,
}): React.ReactElement<WikiBoxInfoBoxItemProps> {
    const { label, value } = data;
    const [isChoplinesToggled, setIsChoplinesToggled] = useState<boolean>(false);
    const containerRef = useRef<HTMLElement>();

    const isTextURL = useMemo(() => {
        if (value.type === wikiBoxInfoBoxItemType.text) {
            return isValidUrl(value.display);
        }

        return false;
    }, [value]);

    // eslint-disable-next-line
    const isLocationCoordinates = useMemo(() => {
        return label === 'Coordinates';
    });

    const shouldKeepOriginalUrl = (url) => {
        return url && url.match(/^(http|https):\/\/www\.youtube\.com/);
    };

    const onLinkClick = (e) => {
        if (shouldKeepOriginalUrl(e?.target?.href)) {
            return;
        }
        if (e?.target?.innerText && makeSearch) {
            e.preventDefault();
            makeSearch(e.target.innerText);
        }
    };

    useEffect(() => {
        if (shouldToggleChoplines) {
            setIsChoplinesToggled(false);
            onChoplinesToggled && onChoplinesToggled();
        }
    }, [shouldToggleChoplines, onChoplinesToggled]);

    useEffect(() => {
        if (openInNewTab || !containerRef.current) {
            return;
        }
        const aList = containerRef.current?.querySelectorAll('a');
        if (!aList) {
            return;
        }
        aList.forEach((a) => a.addEventListener('click', onLinkClick));
        return () => {
            aList.forEach((a) => a.removeEventListener('click', onLinkClick));
        };
        // eslint-disable-next-line
    }, [data]);

    const showMoreChopLines = () => {
        setIsChoplinesToggled(true);
        onShowMore && setTimeout(() => onShowMore(), 100);
    };

    return (
        <tr>
            <td>
                <div className="wikiBox__text" ref={containerRef}>
                    <ChopLinesExtended
                        lines={2}
                        lineHeight={20}
                        disabled={isChoplinesToggled}
                        ellipsis={
                            <button onClick={showMoreChopLines} className="wikiBox__more-mobile">
                                ...
                            </button>
                        }
                    >
                        <WikiBoxInfoBoxLinkRichItem
                            label={label}
                            links={value.links}
                            addColonMark={true}
                            className="wikiBox__caption"
                            openInNewTab={openInNewTab}
                            encryptionDetails={encryptionDetails}
                        />
                        {value.type === wikiBoxInfoBoxItemType.text && !isTextURL && !isLocationCoordinates && (
                            <WikiBoxInfoBoxTextItem
                                label={label}
                                value={value}
                                openInNewTab={openInNewTab}
                                encryptionDetails={encryptionDetails}
                            />
                        )}
                        {value.type === wikiBoxInfoBoxItemType.text && isLocationCoordinates && (
                            <WikiBoxInfoBoxCoordinatesLinkItem
                                label={value.display}
                                dataType="link"
                                topicTitle={topicTitle}
                            />
                        )}
                        {value.type === wikiBoxInfoBoxItemType.list && (
                            <WikiBoxInfoBoxListItem
                                label={label}
                                value={value}
                                encryptionDetails={encryptionDetails}
                                openInNewTab={openInNewTab}
                            />
                        )}
                        {/* In case if type provided as text but display value is link */}
                        {(value.type === wikiBoxInfoBoxItemType.link || isTextURL) && (
                            <WikiBoxInfoBoxLinkItem
                                dataType="link"
                                label={value.display}
                                url={value.url}
                                encryptionDetails={encryptionDetails}
                                openInNewTab={openInNewTab}
                            />
                        )}
                    </ChopLinesExtended>
                </div>
            </td>
        </tr>
    );
}
